// ─────────────────────────────────────────────────────────────────────────────
// feeApi.js
// ALL backend calls for Fee Management live here.
// Each function has a TODO comment showing the real HTTP call.
// The localStorage blocks beneath each TODO are the dev-phase fallback —
// delete them once your backend is ready and uncomment the fetch() calls.
// ─────────────────────────────────────────────────────────────────────────────

// const BASE_URL = "https://api.yourschool.com/v1";  // ← set before going live

// ── localStorage helpers (dev only — remove when backend is live) ─────────────
const _get  = (key, fb)  => { try { return JSON.parse(localStorage.getItem(key)) ?? fb; } catch { return fb; } };
const _set  = (key, val) => { try { localStorage.setItem(key, JSON.stringify(val)); } catch {} };
const STORE = {
  STUDENTS:        "students",
  FEE_ASSIGNMENTS: "fee_assignments_v2",
  PAYMENTS:        "payment_history",
  PREV_DUES:       (id) => `previous_dues_${id}`,
};

// ── Concession helper ─────────────────────────────────────────────────────────
const calcConcession = (subtotal, concession) => {
  if (!concession) return 0;
  if (concession.type === "percentage") return (subtotal * concession.value) / 100;
  if (concession.type === "fixed")      return Math.min(concession.value, subtotal);
  if (concession.type === "waiver")     return subtotal;
  return 0;
};

/** Merge new unpaid fee into an existing unpaid assignment for the same month. */
const upsertStudentMonthAssignment = (all, studentMeta, payload) => {
  const sid = String(studentMeta.studentId);
  if (!all[sid]) all[sid] = {};

  const existingKey = Object.keys(all[sid]).find((key) => {
    const entry = all[sid][key];
    return entry?.month === payload.month && entry?.status !== "Paid" && entry?.status !== "Waived";
  });

  const mergeComponents = (base = [], incoming = []) => {
    const map = new Map();
    [...base, ...incoming].forEach((comp) => {
      const key = `${comp.componentId || ""}|${(comp.componentLabel || "").toLowerCase()}`;
      map.set(key, { ...comp });
    });
    return Array.from(map.values());
  };

  if (existingKey) {
    const existing = all[sid][existingKey];
    const selectedCategories = [...new Set([
      ...(existing.selectedCategories || [existing.categoryId].filter(Boolean)),
      ...(payload.selectedCategories || [payload.categoryId].filter(Boolean)),
    ])];
    const components = mergeComponents(existing.components || [], payload.components || []);
    const subtotal = components.reduce((s, c) => s + (c.amount || 0), 0);
    const concession = payload.concession || existing.concession || null;
    const concessionAmount = calcConcession(subtotal, concession);
    const labels = selectedCategories.map((id) => {
      if (id === payload.categoryId) return payload.categoryLabel;
      if (id === existing.categoryId) return existing.categoryLabel;
      return id;
    });
    // Prefer readable labels from known categoryLabel strings on both sides
    const labelParts = [
      ...(existing.categoryLabel || "").split(" + ").map((s) => s.trim()).filter(Boolean),
      ...(payload.categoryLabel || "").split(" + ").map((s) => s.trim()).filter(Boolean),
    ];
    const uniqueLabels = [...new Set(labelParts.length ? labelParts : labels)];

    const assignment = {
      ...existing,
      ...studentMeta,
      categoryId: selectedCategories.length > 1 ? "custom" : (selectedCategories[0] || payload.categoryId),
      categoryLabel: uniqueLabels.join(" + "),
      categoryPrefix: selectedCategories.length > 1 ? "MIX" : (payload.categoryPrefix || existing.categoryPrefix),
      selectedCategories,
      components,
      academicYear: payload.academicYear || existing.academicYear,
      month: payload.month,
      dueDate: payload.dueDate || existing.dueDate || "10",
      concession,
      subtotal,
      concessionAmount,
      netPayable: subtotal - concessionAmount,
      status: existing.status || "Unpaid",
      lastUpdated: new Date().toISOString(),
    };

    // Re-key if category id changed (e.g. single → mixed)
    const newKey = `${assignment.categoryId}_${assignment.month}`;
    if (existingKey !== newKey) delete all[sid][existingKey];
    all[sid][newKey] = assignment;
    return assignment;
  }

  const subtotal = payload.components.reduce((s, c) => s + c.amount, 0);
  const concessionAmount = calcConcession(subtotal, payload.concession);
  const assignment = {
    ...payload,
    ...studentMeta,
    selectedCategories: payload.selectedCategories || [payload.categoryId].filter(Boolean),
    subtotal,
    concessionAmount,
    netPayable: subtotal - concessionAmount,
    status: "Unpaid",
    lastUpdated: new Date().toISOString(),
  };
  all[sid][`${payload.categoryId}_${payload.month}`] = assignment;
  return assignment;
};

// ─────────────────────────────────────────────────────────────────────────────
// STUDENTS
// ─────────────────────────────────────────────────────────────────────────────

/** GET /students */
export const fetchStudents = () => {
  // TODO: return fetch(`${BASE_URL}/students`).then(r => r.json());
  return Promise.resolve(_get(STORE.STUDENTS, []));
};

// ─────────────────────────────────────────────────────────────────────────────
// FEE ASSIGNMENTS  —  shape: { [studentId]: { [categoryId_month]: Assignment } }
// ─────────────────────────────────────────────────────────────────────────────

/** GET /fee-assignments */
export const fetchFeeAssignments = () => {
  // TODO: return fetch(`${BASE_URL}/fee-assignments`).then(r => r.json());
  return Promise.resolve(_get(STORE.FEE_ASSIGNMENTS, {}));
};

/**
 * Consolidate all Unpaid fee entries that share the same month for each student
 * into a single combined assignment so they can be paid as one challan.
 */
export const consolidateSameMonthUnpaidFees = (studentId = null) => {
  const all = _get(STORE.FEE_ASSIGNMENTS, {});
  const sids = studentId ? [String(studentId)] : Object.keys(all);
  let changed = false;

  sids.forEach((sid) => {
    const entries = all[sid] || {};
    const byMonth = {};
    Object.entries(entries).forEach(([key, entry]) => {
      if (!entry?.month) return;
      if (entry.status === "Paid" || entry.status === "Waived") return;
      if (!byMonth[entry.month]) byMonth[entry.month] = [];
      byMonth[entry.month].push({ key, entry });
    });

    Object.entries(byMonth).forEach(([month, list]) => {
      if (list.length < 2) return;
      changed = true;
      const base = list[0].entry;
      const componentsMap = new Map();
      const selected = new Set();
      const labels = new Set();
      list.forEach(({ entry }) => {
        (entry.selectedCategories || [entry.categoryId].filter(Boolean)).forEach((id) => selected.add(id));
        (entry.categoryLabel || "").split(" + ").map((s) => s.trim()).filter(Boolean).forEach((l) => labels.add(l));
        (entry.components || []).forEach((comp) => {
          const k = `${comp.componentId || ""}|${(comp.componentLabel || "").toLowerCase()}`;
          componentsMap.set(k, { ...comp });
        });
      });
      const components = Array.from(componentsMap.values());
      const subtotal = components.reduce((s, c) => s + (c.amount || 0), 0);
      const concession = base.concession || null;
      const concessionAmount = calcConcession(subtotal, concession);
      const selectedCategories = [...selected];
      const assignment = {
        ...base,
        categoryId: selectedCategories.length > 1 ? "custom" : (selectedCategories[0] || base.categoryId),
        categoryLabel: [...labels].join(" + ") || base.categoryLabel,
        categoryPrefix: selectedCategories.length > 1 ? "MIX" : (base.categoryPrefix || "FEE"),
        selectedCategories,
        components,
        month,
        subtotal,
        concessionAmount,
        netPayable: subtotal - concessionAmount,
        status: "Unpaid",
        lastUpdated: new Date().toISOString(),
      };
      list.forEach(({ key }) => { delete all[sid][key]; });
      all[sid][`${assignment.categoryId}_${month}`] = assignment;
    });
  });

  if (changed) _set(STORE.FEE_ASSIGNMENTS, all);
  return Promise.resolve(all);
};

/**
 * POST /fee-assignments/student
 * Body: { studentId, categoryId, categoryLabel, categoryPrefix,
 *         components, academicYear, month, dueDate, concession }
 */
export const assignFeeToStudent = (payload) => {
  // TODO: return fetch(`${BASE_URL}/fee-assignments/student`, {
  //   method: "POST", headers: { "Content-Type": "application/json" },
  //   body: JSON.stringify(payload),
  // }).then(r => r.json());

  const all = _get(STORE.FEE_ASSIGNMENTS, {});
  const assignment = upsertStudentMonthAssignment(all, {
    studentId: payload.studentId,
    studentName: payload.studentName,
    studentClass: payload.studentClass,
    studentRollNo: payload.studentRollNo,
    studentSection: payload.studentSection,
  }, payload);
  _set(STORE.FEE_ASSIGNMENTS, all);
  return Promise.resolve(assignment);
};

/**
 * POST /fee-assignments/class
 * Body: { className, categoryId, ... }
 */
export const assignFeeToClass = (payload, students) => {
  // TODO: return fetch(`${BASE_URL}/fee-assignments/class`, {
  //   method: "POST", headers: { "Content-Type": "application/json" },
  //   body: JSON.stringify(payload),
  // }).then(r => r.json());

  const all     = _get(STORE.FEE_ASSIGNMENTS, {});
  const results = [];
  students.filter(s => s.class === payload.className).forEach(student => {
    const assignment = upsertStudentMonthAssignment(all, {
      studentId: student.id,
      studentName: student.name,
      studentClass: student.class,
      studentRollNo: student.rollNo,
      studentSection: student.section,
    }, payload);
    results.push(assignment);
  });
  _set(STORE.FEE_ASSIGNMENTS, all);
  return Promise.resolve(results);
};

/**
 * POST /fee-assignments/bulk
 * Body: { studentIds, categoryId, categoryLabel, categoryPrefix,
 *         components, academicYear, month, dueDate, concession }
 * Assigns/overwrites one category+month fee entry across an explicit list of
 * students — used for both class-wide bulk-assign and ad-hoc multi-select
 * bulk edit (same semantics as assignFeeToClass, just keyed off an id list).
 */
export const assignFeeToStudents = (payload, studentIds, students) => {
  // TODO: return fetch(`${BASE_URL}/fee-assignments/bulk`, {
  //   method: "POST", headers: { "Content-Type": "application/json" },
  //   body: JSON.stringify({ ...payload, studentIds }),
  // }).then(r => r.json());

  const all       = _get(STORE.FEE_ASSIGNMENTS, {});
  const idSet     = new Set(studentIds.map(String));
  const results   = [];
  students.filter(s => idSet.has(String(s.id))).forEach(student => {
    const assignment = upsertStudentMonthAssignment(all, {
      studentId: student.id,
      studentName: student.name,
      studentClass: student.class,
      studentRollNo: student.rollNo,
      studentSection: student.section,
    }, payload);
    results.push(assignment);
  });
  _set(STORE.FEE_ASSIGNMENTS, all);
  return Promise.resolve(results);
};

/**
 * DELETE /fee-assignments/bulk
 * Body: { deletions: [{ studentId, categoryId, month }] }
 */
export const deleteFeeEntries = (deletions) => {
  // TODO: return fetch(`${BASE_URL}/fee-assignments/bulk`, {
  //   method: "DELETE", headers: { "Content-Type": "application/json" },
  //   body: JSON.stringify({ deletions }),
  // }).then(r => r.json());

  const all = _get(STORE.FEE_ASSIGNMENTS, {});
  deletions.forEach(({ studentId, categoryId, month }) => {
    const sid = String(studentId);
    const key = `${categoryId}_${month}`;
    if (all[sid]?.[key]) {
      delete all[sid][key];
      if (!Object.keys(all[sid]).length) delete all[sid];
    }
  });
  _set(STORE.FEE_ASSIGNMENTS, all);
  return Promise.resolve({ success: true, count: deletions.length });
};

/**
 * PATCH /fee-assignments/:studentId/:categoryId/:month/components/:componentId
 * Body: { amount }
 */
export const updateComponent = (studentId, categoryId, month, componentId, amount) => {
  // TODO: return fetch(`${BASE_URL}/fee-assignments/${studentId}/${categoryId}/${month}/components/${componentId}`, {
  //   method: "PATCH", headers: { "Content-Type": "application/json" },
  //   body: JSON.stringify({ amount }),
  // }).then(r => r.json());

  const all   = _get(STORE.FEE_ASSIGNMENTS, {});
  const entry = all[String(studentId)]?.[`${categoryId}_${month}`];
  if (entry) {
    const comp = entry.components.find(c => c.componentId === componentId);
    if (comp) comp.amount = amount;
    entry.subtotal         = entry.components.reduce((s, c) => s + c.amount, 0);
    entry.concessionAmount = calcConcession(entry.subtotal, entry.concession);
    entry.netPayable       = entry.subtotal - entry.concessionAmount;
    entry.lastUpdated      = new Date().toISOString();
    _set(STORE.FEE_ASSIGNMENTS, all);
  }
  return Promise.resolve(entry);
};

/**
 * DELETE /fee-assignments/:studentId/:categoryId/:month/components/:componentId
 */
export const removeComponent = (studentId, categoryId, month, componentId) => {
  // TODO: return fetch(`${BASE_URL}/fee-assignments/${studentId}/${categoryId}/${month}/components/${componentId}`, { method: "DELETE" }).then(r => r.json());

  const all   = _get(STORE.FEE_ASSIGNMENTS, {});
  const entry = all[String(studentId)]?.[`${categoryId}_${month}`];
  if (entry) {
    entry.components       = entry.components.filter(c => c.componentId !== componentId);
    entry.subtotal         = entry.components.reduce((s, c) => s + c.amount, 0);
    entry.concessionAmount = calcConcession(entry.subtotal, entry.concession);
    entry.netPayable       = entry.subtotal - entry.concessionAmount;
    _set(STORE.FEE_ASSIGNMENTS, all);
  }
  return Promise.resolve(entry);
};

/**
 * PATCH /fee-assignments/:studentId/:categoryId/:month/concession
 * Body: { concession }  — null to remove
 */
export const updateConcession = (studentId, categoryId, month, concession) => {
  // TODO: return fetch(`${BASE_URL}/fee-assignments/${studentId}/${categoryId}/${month}/concession`, {
  //   method: "PATCH", headers: { "Content-Type": "application/json" },
  //   body: JSON.stringify({ concession }),
  // }).then(r => r.json());

  const all   = _get(STORE.FEE_ASSIGNMENTS, {});
  const entry = all[String(studentId)]?.[`${categoryId}_${month}`];
  if (entry) {
    entry.concession       = concession;
    entry.concessionAmount = calcConcession(entry.subtotal, concession);
    entry.netPayable       = entry.subtotal - entry.concessionAmount;
    entry.lastUpdated      = new Date().toISOString();
    _set(STORE.FEE_ASSIGNMENTS, all);
  }
  return Promise.resolve(entry);
};

/**
 * PATCH /fee-assignments/:studentId/:categoryId/:month/due-date
 * Body: { dueDate }
 */
export const updateDueDate = (studentId, categoryId, month, dueDate) => {
  // TODO: return fetch(`${BASE_URL}/fee-assignments/${studentId}/${categoryId}/${month}/due-date`, {
  //   method: "PATCH", headers: { "Content-Type": "application/json" },
  //   body: JSON.stringify({ dueDate }),
  // }).then(r => r.json());

  const all   = _get(STORE.FEE_ASSIGNMENTS, {});
  const entry = all[String(studentId)]?.[`${categoryId}_${month}`];
  if (entry) { entry.dueDate = dueDate; entry.lastUpdated = new Date().toISOString(); _set(STORE.FEE_ASSIGNMENTS, all); }
  return Promise.resolve(entry);
};

/**
 * PATCH /fee-assignments/:studentId/:categoryId/:month/status
 * Body: { status, paymentMethod, paymentDate, remarks, feeId? }
 */
export const updateFeeStatus = (studentId, categoryId, month, status, details = {}) => {
  const all = _get(STORE.FEE_ASSIGNMENTS, {});
  const sid = String(studentId);
  if (!all[sid]) return Promise.resolve(null);

  // Resolve the real storage key — after merges the categoryId may be "custom"
  // while the caller still passes an older id, so fall back carefully.
  let key = details.feeId || `${categoryId}_${month}`;
  let entry = all[sid][key];

  if (!entry) {
    const match = Object.entries(all[sid]).find(([k, e]) => {
      if (details.feeId && k === details.feeId) return true;
      if (month && e.month === month && String(e.categoryId) === String(categoryId)) return true;
      if (month && e.month === month && k.endsWith(`_${month}`)) return true;
      return false;
    });
    if (match) {
      key = match[0];
      entry = match[1];
    }
  }

  if (entry) {
    entry.status = status || "Unpaid";
    entry.paymentMethod = entry.status === "Paid" ? (details.paymentMethod || entry.paymentMethod || "cash") : null;
    entry.paymentDate = entry.status === "Paid" ? (details.paymentDate || entry.paymentDate || new Date().toISOString().split("T")[0]) : null;
    entry.remarks = details.remarks ?? entry.remarks ?? null;
    entry.lastUpdated = new Date().toISOString();
    // Keep categoryId on the entry in sync with the storage key prefix when possible
    if (!entry.categoryId && categoryId) entry.categoryId = categoryId;
    _set(STORE.FEE_ASSIGNMENTS, all);

    const payments = _get(STORE.PAYMENTS, []);
    const isSameFee = (payment) => String(payment.studentId) === sid && payment.feeId === key;

    if (entry.status === "Paid") {
      const existingPayment = payments.find(isSameFee);
      const paymentRecord = {
        id: existingPayment?.id || Date.now(),
        studentId,
        feeId: key,
        amount: entry.netPayable || 0,
        method: entry.paymentMethod,
        remarks: entry.remarks,
        date: entry.paymentDate ? `${entry.paymentDate}T00:00:00.000Z` : new Date().toISOString(),
      };

      _set(
        STORE.PAYMENTS,
        existingPayment
          ? payments.map(payment => isSameFee(payment) ? { ...payment, ...paymentRecord } : payment)
          : [...payments, paymentRecord]
      );
    } else {
      _set(STORE.PAYMENTS, payments.filter(payment => !isSameFee(payment)));
    }

    try {
      window.dispatchEvent(new CustomEvent("fee-status-updated", { detail: { studentId: sid, feeId: key, status: entry.status } }));
    } catch {}
  }

  return Promise.resolve(entry);
};

/**
 * DELETE /fee-assignments/:studentId/:categoryId/:month
 */
export const deleteFeeEntry = (studentId, categoryId, month) => {
  // TODO: return fetch(`${BASE_URL}/fee-assignments/${studentId}/${categoryId}/${month}`, { method: "DELETE" }).then(r => r.json());

  const all = _get(STORE.FEE_ASSIGNMENTS, {});
  const sid = String(studentId);
  const key = `${categoryId}_${month}`;
  if (all[sid]?.[key]) {
    delete all[sid][key];
    if (!Object.keys(all[sid]).length) delete all[sid];
    _set(STORE.FEE_ASSIGNMENTS, all);
  }
  return Promise.resolve({ success: true });
};

// ─────────────────────────────────────────────────────────────────────────────
// PAYMENTS
// ─────────────────────────────────────────────────────────────────────────────

/** GET /payments?studentId=:id */
export const fetchPayments = (studentId) => {
  // TODO: return fetch(`${BASE_URL}/payments?studentId=${studentId}`).then(r => r.json());
  return Promise.resolve(_get(STORE.PAYMENTS, []).filter(p => String(p.studentId) === String(studentId)));
};

/**
 * POST /payments
 * Body: { studentId, feeId, amount, method, remarks, date }
 */
export const recordPayment = (payload) => {
  // TODO: return fetch(`${BASE_URL}/payments`, {
  //   method: "POST", headers: { "Content-Type": "application/json" },
  //   body: JSON.stringify(payload),
  // }).then(r => r.json());

  const record = { id: Date.now(), ...payload, date: new Date().toISOString() };
  _set(STORE.PAYMENTS, [..._get(STORE.PAYMENTS, []), record]);
  // Mark assignment as Paid
  const all = _get(STORE.FEE_ASSIGNMENTS, {});
  const sid = String(payload.studentId);
  if (all[sid]?.[payload.feeId]) {
    all[sid][payload.feeId].status      = "Paid";
    all[sid][payload.feeId].paymentDate = record.date.split("T")[0];
    _set(STORE.FEE_ASSIGNMENTS, all);
  }
  return Promise.resolve(record);
};

/** GET /students/:id/previous-dues */
export const fetchPreviousDues = (studentId) => {
  // TODO: return fetch(`${BASE_URL}/students/${studentId}/previous-dues`).then(r => r.json());
  return Promise.resolve(_get(STORE.PREV_DUES(studentId), []));
};

/** PUT /students/:id/previous-dues  Body: { dues } */
export const savePreviousDues = (studentId, dues) => {
  // TODO: return fetch(`${BASE_URL}/students/${studentId}/previous-dues`, {
  //   method: "PUT", headers: { "Content-Type": "application/json" },
  //   body: JSON.stringify({ dues }),
  // }).then(r => r.json());
  _set(STORE.PREV_DUES(studentId), dues);
  return Promise.resolve(dues);
};
